import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../api/client';

export type Loaded<T> =
  | { state: 'loading' }
  | { state: 'ok'; data: T }
  | { state: 'error'; status: number; message: string };

type Call<T> = () => Promise<{ data?: T; error?: unknown; response: Response }>;

/**
 * Run one generated-client call when `key` changes; `reload` runs it again. With `keepWhile`, a
 * new key whose `keepWhile` is unchanged keeps the current data on screen, like a reload (e.g.
 * another filter of the same board).
 */
export function useLoad<T>(call: Call<T>, key: string, keepWhile?: string): [Loaded<T>, () => void] {
  const [result, setResult] = useState<Loaded<T>>({ state: 'loading' });
  const [tick, setTick] = useState(0);
  const last = useRef(key);
  const lastKeep = useRef(keepWhile);
  useEffect(() => {
    let live = true;
    // A new key shows loading; a reload keeps the current data on screen until the answer comes.
    if (last.current !== key) {
      last.current = key;
      const keep = keepWhile !== undefined && lastKeep.current === keepWhile;
      if (!keep) setResult({ state: 'loading' });
    }
    lastKeep.current = keepWhile;
    call()
      .then(({ data, error, response }) => {
        if (!live) return;
        if (data !== undefined) setResult({ state: 'ok', data });
        else setResult({ state: 'error', status: response.status, message: errorMessage(error, response.status) });
      })
      .catch(() => live && setResult({ state: 'error', status: 0, message: errorMessage(null) }));
    return () => {
      live = false;
    };
    // `call` is a fresh closure each render; `key` names what it fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return [result, reload];
}
